import React, { useRef, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import './FullCalendarView.css';

const FullCalendarView = ({ posts, onEventClick, currentMonth, onMonthChange }) => {
    const calendarRef = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isSmallMobile = useMediaQuery('(max-width: 400px)');

    // Convert posts to FullCalendar events format
    // day_offset is the number of days from today (0 = today, 1 = tomorrow, etc.)
    const events = posts.map((post, index) => {
        // Determine event color based on post type
        let color = '#9C27B0'; // Default purple
        if (post.is_ai_generated) color = '#00BCD4'; // Cyan for AI
        if (post.is_festival) color = '#E91E63'; // Pink for festivals
        if (post.is_testimonial) color = '#4CAF50'; // Green for testimonials
        if (post.is_gmb_photo) color = '#2196F3'; // Blue for GMB photos

        // Calculate scheduled date from day_offset (days from today)
        const dayOffset = post.day_offset ?? 0;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const scheduledDate = new Date(today.getTime() + (dayOffset * 24 * 60 * 60 * 1000));

        return {
            id: `${post.id || index}`,
            title: post.title || `Post ${index + 1}`,
            start: scheduledDate,
            allDay: true,
            backgroundColor: color,
            borderColor: color,
            textColor: '#ffffff',
            extendedProps: {
                post: post,
                type: post.is_festival ? 'festival' :
                    post.is_ai_generated ? 'ai' :
                        post.is_testimonial ? 'testimonial' :
                            post.is_gmb_photo ? 'photo' : 'other'
            }
        };
    });

    // Navigate calendar when currentMonth changes
    useEffect(() => {
        if (calendarRef.current) {
            const calendarApi = calendarRef.current.getApi();
            calendarApi.gotoDate(currentMonth);
        }
    }, [currentMonth]);

    const handleEventClick = (info) => {
        if (onEventClick) {
            onEventClick(info.event.extendedProps.post);
        }
    };

    // Mobile-optimized calendar configuration
    const mobileHeaderToolbar = {
        left: 'prev',
        center: 'title',
        right: 'next'
    };

    const desktopHeaderToolbar = {
        left: 'prev,next today',
        center: 'title',
        right: ''
    };

    // Day header format - shorter on mobile
    const dayHeaderFormat = isSmallMobile
        ? { weekday: 'narrow' } // S, M, T, W, T, F, S
        : isMobile
            ? { weekday: 'short' } // Sun, Mon, Tue
            : { weekday: 'short' }; // Sun, Mon, Tue

    return (
        <Box className="fullcalendar-wrapper">
            <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                initialDate={currentMonth}
                events={events}
                eventClick={handleEventClick}
                headerToolbar={isMobile ? mobileHeaderToolbar : desktopHeaderToolbar}
                height="auto"
                eventDisplay="block"
                dayMaxEvents={isMobile ? 2 : 3}
                moreLinkText={isMobile ? '+' : 'more'}
                dayHeaderFormat={dayHeaderFormat}
                titleFormat={{ year: 'numeric', month: isMobile ? 'short' : 'long' }}
                fixedWeekCount={false}
                showNonCurrentDates={!isSmallMobile}
                eventTimeFormat={{
                    hour: 'numeric',
                    minute: '2-digit',
                    meridiem: 'short'
                }}
                eventContent={(eventInfo) => {
                    return (
                        <Box className="fc-event-custom">
                            {!isMobile && <Box className="fc-event-time">5:30a</Box>}
                            <Box className="fc-event-title">{eventInfo.event.title}</Box>
                        </Box>
                    );
                }}
            />
        </Box>
    );
};

export default FullCalendarView;
